import { NgClass } from '@angular/common'
import { Component, computed, DestroyRef, effect, inject, input, output } from '@angular/core'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { EventApp, UpdateEventAppPayload } from '@app/v2/event/types/event-app.type'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { TruncationDetectorDirective } from '@app/v2/shared/directives/truncation-detector.directive'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { VIRTUAL_SERVICES } from '@events/repositories/virtual-services.repository'
import { NgxTippyModule } from 'ngx-tippy-wrapper'

@Component({
  selector: 'app-event-app-card',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, BasicInputComponent, ButtonSpinnerDirective, NgxTippyModule, TruncationDetectorDirective],
  templateUrl: './event-app-card.component.html',
  styleUrl: './event-app-card.component.scss'
})
export class EventAppCardComponent {
  public readonly eventApp = input.required<EventApp>()
  public readonly handshakeSettingsLoading = input<boolean>(false)
  public readonly snoballSettingsLoading = input<boolean>(false)
  public readonly snoballActivateLoading = input<boolean>(false)
  public readonly removed = output<EventApp>()
  public readonly handshakeSettings = output<EventApp>()
  public readonly snoballSettings = output<EventApp>()
  public readonly snoballActivate = output<EventApp>()
  public readonly updated = output<UpdateEventAppPayload>()

  public readonly BasicFieldType = BasicFieldType
  public readonly BasicInputMode = BasicInputMode

  public readonly nameControl = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] })
  public readonly linkControl = new FormControl<string>('', { nonNullable: true })
  public readonly catalog = computed(() => VIRTUAL_SERVICES.find((service) => service.id === this.eventApp().location_details.service.id))

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _blurSaveDelayMs = 150
  private _blurSaveTimeout: ReturnType<typeof setTimeout> | null = null
  private _pendingBlurPayload: UpdateEventAppPayload | null = null
  private _cancelNextBlurSave = false

  constructor() {
    effect(() => {
      this.nameControl.setValue(this.eventApp().name ?? '', { emitEvent: false })
      this.linkControl.setValue(this.eventApp().location_details?.link ?? '', { emitEvent: false })
      this._setLinkValidatorsFromCatalog()
    })
    this._destroyRef.onDestroy(() => {
      if (this._blurSaveTimeout !== null) clearTimeout(this._blurSaveTimeout)
    })
  }

  /**
   * Checks if the target link field should be shown based on the catalog entry.
   * @returns {boolean} - True if the target link field should be shown, false otherwise.
   */
  public showTargetLinkField(): boolean {
    return this.catalog()?.regex != null
  }

  /**
   * Gets the placeholder from the catalog entry.
   * @returns {string} - The placeholder from the catalog entry.
   */
  public linkPlaceholder(): string {
    return this.catalog()?.placeholder ?? 'Target link'
  }

  /**
   * Sets the link validators based on the catalog entry.
   * @returns {void}
   * `Validators.required` + `Validators.pattern(regex)` when catalog defines `regex`.
   */
  private _setLinkValidatorsFromCatalog(): void {
    const regex = this.catalog()?.regex
    if (!regex) this.linkControl.clearValidators()
    else this.linkControl.setValidators([Validators.required, Validators.pattern(regex)])
    this.linkControl.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * Cover or logo used as the marketplace-style hero image.
   * @param {EventApp} app - The added event app.
   * @returns {string}
   */
  public heroImageSrc(app: EventApp): string {
    const catalog = this.catalog()
    return catalog?.coverImageUrl || app.location_details.service.coverImageUrl || app.location_details.service.logo
  }

  /**
   * Object-fit classes for the hero image, matching the marketplace tiles.
   * @param {EventApp} app - The added event app.
   * @returns {string}
   */
  public heroImageClass(app: EventApp): string {
    const catalog = this.catalog()
    const hasCover = Boolean(catalog?.coverImageUrl || app.location_details.service.coverImageUrl)
    const objectFit = catalog?.coverObjectFit === 'cover' ? 'tw-object-cover' : 'tw-object-contain'
    return hasCover ? objectFit : `${objectFit} tw-p-6`
  }

  /**
   * Marketplace description for the added app card.
   * @param {EventApp} app - The added event app.
   * @returns {string}
   */
  public descriptionText(app: EventApp): string {
    const catalog = this.catalog()
    const service = app.location_details.service
    return catalog?.description ?? service.description ?? catalog?.informationTemplate ?? service.informationTemplate ?? ''
  }

  /**
   * Checks if the handshake settings should be shown for the app.
   * @param {EventApp} app - The app to check.
   * @returns {boolean} - True if the handshake settings should be shown, false otherwise.
   */
  public showHandshakeSettings(app: EventApp): boolean {
    return app.location_details.service.slug === 'handshake'
  }

  /**
   * Checks if the Snöball settings should be shown for the app.
   * @param {EventApp} app - The app to check.
   * @returns {boolean} - True if the Snöball settings should be shown.
   */
  public showSnoballSettings(app: EventApp): boolean {
    return app.location_details.service.slug === 'snoball'
  }

  /**
   * Whether the session title is read-only. Handshake and Snöball keep a fixed name.
   * @param {EventApp} app - The app to check.
   * @returns {boolean}
   */
  public isNameReadOnly(app: EventApp): boolean {
    const slug = app.location_details?.service?.slug
    return slug === 'handshake' || slug === 'snoball'
  }

  /**
   * Whether a click on the card body should open the integration settings.
   * @param {EventApp} app - The app to check.
   * @returns {boolean}
   */
  public opensSettingsOnCardClick(app: EventApp): boolean {
    return this.showSnoballSettings(app)
  }

  /**
   * Opens Snöball settings when the card body is clicked.
   * @param {EventApp} app - The app that was clicked.
   */
  public onCardClick(app: EventApp): void {
    if (!this.opensSettingsOnCardClick(app)) return
    this.onSnoballSettings()
  }

  /**
   * Removes the app.
   * @returns {void}
   */
  public onRemove(): void {
    this.removed.emit(this.eventApp())
  }

  /**
   * Opens the handshake settings modal.
   * @returns {void}
   */
  public onHandshakeSettings(): void {
    this.handshakeSettings.emit(this.eventApp())
  }

  /**
   * Opens the Snöball configuration modal.
   * @returns {void}
   */
  public onSnoballSettings(): void {
    this.snoballSettings.emit(this.eventApp())
  }

  /**
   * Sends the Snöball activation request from the card.
   * @returns {void}
   */
  public onSnoballActivate(): void {
    this.snoballActivate.emit(this.eventApp())
  }

  /**
   * Schedules an update emit when either the name or link actually changed. Marks both
   * controls as touched so any required/pattern errors render, and only saves
   * when both controls are valid (e.g. blocks save when link is required-empty).
   * The small delay lets the wizard close flow cancel the save and show the
   * unsaved-changes modal instead.
   * @returns {void}
   */
  public onBlur(): void {
    const payload = this.getUnsavedPayload()
    if (!payload) return
    this._scheduleBlurSave(payload)
  }

  /**
   * Submits on Enter by re-using {@link onBlur} directly.
   * @param {KeyboardEvent} event - Keydown event from the input.
   * @returns {void}
   */
  public onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return
    event.preventDefault()
    this.onBlur()
  }

  /**
   * Whether the form has unsaved edits compared to the bound `eventApp`.
   * Invalid controls still count as unsaved (so the wizard prompts the user).
   * @returns {boolean}
   */
  public hasUnsavedChanges(): boolean {
    const current = this.eventApp()
    if (!current) return false
    const name = this.nameControl.value
    const link = this.linkControl.value
    const savedLink = current.location_details?.link ?? ''
    return name !== (current.name ?? '') || link !== savedLink
  }

  /**
   * Gets the unsaved payload for the current unsaved edits.
   * @returns {UpdateEventAppPayload | null}
   */
  public getUnsavedPayload(): UpdateEventAppPayload | null {
    const current = this.eventApp()
    if (!current) return null
    if (!this.hasUnsavedChanges()) return null
    const savedLink = current.location_details?.link ?? ''
    const linkChanged = this.linkControl.value !== savedLink

    this.nameControl.markAsTouched()
    if (linkChanged) this.linkControl.markAsTouched()
    if (this.nameControl.invalid) return null
    if (linkChanged && this.linkControl.invalid) return null
    return {
      name: this.nameControl.value,
      location_details: { ...current.location_details, link: this.linkControl.value }
    }
  }

  /**
   * Cancels any pending blur-triggered save so the parent can decide whether
   * to commit (Save changes) or drop (Discard) the edits via the modal.
   * @returns {void}
   */
  public cancelPendingBlurSave(): void {
    this._cancelNextBlurSave = true
    this._pendingBlurPayload = null
    if (this._blurSaveTimeout !== null) {
      clearTimeout(this._blurSaveTimeout)
      this._blurSaveTimeout = null
    }
  }

  /**
   * Reverts the form controls back to the values from the bound `eventApp`.
   * @returns {void}
   */
  public revertToSaved(): void {
    const current = this.eventApp()
    if (!current) return
    this.nameControl.setValue(current.name ?? '', { emitEvent: false })
    this.linkControl.setValue(current.location_details?.link ?? '', { emitEvent: false })
    this.nameControl.markAsPristine()
    this.linkControl.markAsPristine()
    this.nameControl.markAsUntouched()
    this.linkControl.markAsUntouched()
  }

  /**
   * Schedules the emit so a wizard close flow can intercept and cancel it.
   * @param {UpdateEventAppPayload} payload - The payload to emit.
   * @returns {void}
   */
  private _scheduleBlurSave(payload: UpdateEventAppPayload): void {
    if (this._blurSaveTimeout !== null) clearTimeout(this._blurSaveTimeout)
    this._pendingBlurPayload = payload
    this._blurSaveTimeout = setTimeout(() => {
      this._blurSaveTimeout = null
      const pending = this._pendingBlurPayload
      this._pendingBlurPayload = null
      if (this._cancelNextBlurSave) {
        this._cancelNextBlurSave = false
        return
      }
      if (!pending) return
      this.updated.emit(pending)
    }, this._blurSaveDelayMs)
  }
}
